import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Target, ArrowLeft, Loader2, Copy, CheckCircle2, Download, ChevronDown, ChevronUp, BookOpen, Save, FolderOpen, Trash2, X, Sliders, ClipboardList, Lightbulb, ListChecks } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Checkbox } from '../components/ui/checkbox';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Slider } from '../components/ui/slider';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import SpEdKitLogo from '../components/SpEdKitLogo';
import axios from 'axios';
import { toast } from 'sonner';
import jsPDF from 'jspdf';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const scaffoldingLevels = [
  { value: 'none', label: 'None', description: 'Independent with typical supports' },
  { value: 'light', label: 'Light', description: 'Verbal prompts, visual cues, check-ins' },
  { value: 'moderate', label: 'Moderate', description: 'Modeling, guided practice, small group' },
  { value: 'heaviest', label: 'Heaviest', description: '1:1 support, hand-over-hand, constant prompting' }
];

const IEPGoals = ({ user }) => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [copied, setCopied] = useState(false);
  const [showObjectives, setShowObjectives] = useState(false);
  const [stories, setStories] = useState([]);
  const [selectedStory, setSelectedStory] = useState(null);
  const [goalSource, setGoalSource] = useState('story'); // 'story' or 'standard'
  
  // Scaffolding state
  const [scaffoldingIndex, setScaffoldingIndex] = useState(0);
  const [adjustingScaffolding, setAdjustingScaffolding] = useState(false);
  const [adjustedGoal, setAdjustedGoal] = useState(null);
  
  // Goal Bank state
  const [showGoalBank, setShowGoalBank] = useState(false);
  const [savedGoals, setSavedGoals] = useState([]);
  const [loadingGoalBank, setLoadingGoalBank] = useState(false);
  const [savingGoal, setSavingGoal] = useState(false);
  const [goalTitle, setGoalTitle] = useState('');
  const [showSaveDialog, setShowSaveDialog] = useState(false);
  const [folders, setFolders] = useState([]);
  const [selectedFolderId, setSelectedFolderId] = useState('');
  
  // Additional Resources state
  const [generatingWorksheet, setGeneratingWorksheet] = useState(false);
  const [generatingPBL, setGeneratingPBL] = useState(false);
  const [generatingTaskAnalysis, setGeneratingTaskAnalysis] = useState(false);
  const [worksheetData, setWorksheetData] = useState(null);
  const [pblData, setPblData] = useState(null);
  const [taskAnalysisData, setTaskAnalysisData] = useState(null);
  
  const [formData, setFormData] = useState({
    scenario: '',
    situation_details: '',
    grade_level: '',
    student_strengths: '',
    student_interests: '',
    include_objectives: false,
    custom_context: '',
    goal_domain: '',
    anchor_standard_type: '',
    anchor_standard: ''
  });

  const gradeOptions = [
    'ECE (Early Childhood)', 'Kindergarten', 
    '1st Grade', '2nd Grade', '3rd Grade', '4th Grade', '5th Grade',
    '6th Grade', '7th Grade', '8th Grade', 
    '9th Grade', '10th Grade', '11th Grade', '12th Grade',
    'YA (Young Adult 18-22)'
  ];

  const goalDomains = [
    { category: 'Reading', items: ['Phonics', 'Fluency', 'Reading Comprehension'] },
    { category: 'Math', items: ['Math Calculation', 'Math Applications'] },
    { category: 'Written Language', items: ['Written Expression', 'Spelling', 'Grammar'] },
    { category: 'Communication', items: ['Expressive Language', 'Receptive Language', 'Pragmatic Language'] },
    { category: 'Motor Skills', items: ['Fine Motor', 'Gross Motor'] },
    { category: 'Social-Emotional', items: ['Social Skills', 'Emotional Regulation', 'Self-Advocacy'] },
    { category: 'Executive Function', items: ['Attention/Focus', 'Organization', 'Task Completion'] },
    { category: 'Adaptive/Transition', items: ['Activities of Daily Living', 'Adult Transition', 'Pre-Vocational Skills'] },
    { category: 'Behavior', items: ['Behavior Management', 'Self-Monitoring'] }
  ];

  const anchorStandardTypes = [
    { value: 'CASEL_SEL', label: 'CASEL SEL Standards', description: 'Social-Emotional Learning competencies' },
    { value: 'CCSS_ELA', label: 'CCSS ELA Standards', description: 'Common Core State Standards for English Language Arts' },
    { value: 'CCSS_MATH', label: 'CCSS Math Standards', description: 'Common Core State Standards for Mathematics' },
    { value: 'NGSS', label: 'NGSS Science Standards', description: 'Next Generation Science Standards' },
    { value: 'PKTLF', label: 'PKTLF Standards', description: 'Pre-K Teaching and Learning Framework' }
  ];

  const anchorStandards = {
    CASEL_SEL: [
      'Self-Awareness: Identifying emotions and recognizing strengths',
      'Self-Management: Managing emotions, impulses, and stress',
      'Social Awareness: Understanding perspectives of others',
      'Relationship Skills: Communicating clearly and working cooperatively',
      'Responsible Decision-Making: Making constructive choices'
    ],
    CCSS_ELA: [
      'RL.K-5: Key Ideas and Details in Literature',
      'RI.K-5: Key Ideas and Details in Informational Text',
      'RF.K-5: Foundational Skills - Print Concepts, Phonics, Fluency',
      'W.K-5: Text Types and Purposes in Writing',
      'SL.K-5: Comprehension and Collaboration in Speaking/Listening',
      'L.K-5: Conventions of Standard English'
    ],
    CCSS_MATH: [
      'NBT: Number and Operations in Base Ten',
      'OA: Operations and Algebraic Thinking',
      'NF: Number and Operations - Fractions',
      'MD: Measurement and Data',
      'G: Geometry',
      'MP: Mathematical Practices'
    ],
    NGSS: [
      'PS: Physical Sciences',
      'LS: Life Sciences',
      'ESS: Earth and Space Sciences',
      'ETS: Engineering, Technology, and Applications of Science'
    ],
    PKTLF: [
      'Approaches to Learning: Curiosity, Initiative, Persistence',
      'Social and Emotional Development: Self-Regulation, Relationships',
      'Language and Literacy: Listening, Speaking, Phonological Awareness',
      'Cognition: Math, Science, Social Studies',
      'Physical Development and Health: Motor Skills, Self-Care'
    ]
  };

  const scenarioSuggestions = [
    'Social Skills - Making friends and maintaining friendships',
    'Emotional Regulation - Managing anger and frustration',
    'Communication - Asking for help appropriately',
    'Transitions - Handling changes in routine',
    'Sensory Regulation - Coping with overwhelming environments',
    'Self-Advocacy - Expressing needs and preferences',
    'Executive Function - Completing multi-step tasks',
    'Attention - Staying focused during instruction'
  ];

  useEffect(() => {
    fetchStories();
    fetchGoalBank();
    fetchFolders();
  }, []);

  const fetchStories = async () => {
    try {
      const response = await axios.get(`${API}/stories`, { withCredentials: true });
      setStories(response.data);
    } catch (error) {
      console.error('Error fetching stories:', error);
    }
  };

  const fetchFolders = async () => {
    try {
      const response = await axios.get(`${API}/folders`, { withCredentials: true });
      setFolders(response.data);
    } catch (error) {
      console.error('Error fetching folders:', error);
    }
  };

  const fetchGoalBank = async () => {
    try {
      setLoadingGoalBank(true);
      const response = await axios.get(`${API}/goal-bank`, { withCredentials: true });
      setSavedGoals(response.data);
    } catch (error) {
      console.error('Error fetching goal bank:', error);
    } finally {
      setLoadingGoalBank(false);
    }
  };

  const handleSaveToGoalBank = async () => {
    if (!result || !goalTitle.trim()) {
      toast.error('Please enter a title for the goal');
      return;
    }

    setSavingGoal(true);
    try {
      const goalData = {
        title: goalTitle,
        goal_domain: result.goal_domain || result.scenario || 'General',
        grade_level: result.grade_level,
        scaffolding_level: scaffoldingLevels[scaffoldingIndex].value,
        annual_goal: adjustedGoal?.adjusted_goal || result.iep_goal?.annual_goal,
        objective_1: result.iep_goal?.objective_1,
        objective_2: result.iep_goal?.objective_2,
        sel_standard: result.sel_standard,
        anchor_standard_type: result.anchor_standard_type,
        anchor_standard: result.anchor_standard,
        accommodations: adjustedGoal?.adjusted_goal?.scaffolding_accommodations || result.iep_goal?.accommodations_suggestions,
        tags: [result.goal_domain, result.grade_level].filter(Boolean),
        folder_id: selectedFolderId || null
      };

      await axios.post(`${API}/goal-bank`, goalData, { withCredentials: true });
      
      const folderName = folders.find(f => f.folder_id === selectedFolderId)?.student_name;
      const successMsg = folderName 
        ? `Goal saved to Goal Bank and linked to ${folderName}'s folder!`
        : 'Goal saved to Goal Bank!';
      toast.success(successMsg);
      
      setShowSaveDialog(false);
      setGoalTitle('');
      setSelectedFolderId('');
      fetchGoalBank();
    } catch (error) {
      console.error('Error saving goal:', error);
      toast.error('Failed to save goal');
    } finally {
      setSavingGoal(false);
    }
  };

  const handleLoadFromGoalBank = async (goal) => {
    // Load a saved goal into the result view
    setResult({
      student_name: 'Student',
      grade_level: goal.grade_level,
      goal_domain: goal.goal_domain,
      sel_standard: goal.sel_standard,
      anchor_standard_type: goal.anchor_standard_type,
      anchor_standard: goal.anchor_standard,
      iep_goal: {
        annual_goal: goal.annual_goal,
        objective_1: goal.objective_1,
        objective_2: goal.objective_2,
        accommodations_suggestions: goal.accommodations,
        progress_monitoring: goal.annual_goal?.progress_monitoring || ''
      }
    });
    
    // Set scaffolding level
    const scaffoldIdx = scaffoldingLevels.findIndex(s => s.value === goal.scaffolding_level);
    setScaffoldingIndex(scaffoldIdx >= 0 ? scaffoldIdx : 0);
    
    setShowGoalBank(false);
    toast.success(`Loaded goal: ${goal.title}`);
  };

  const handleDeleteFromGoalBank = async (goalId) => {
    try {
      await axios.delete(`${API}/goal-bank/${goalId}`, { withCredentials: true });
      toast.success('Goal deleted from Goal Bank');
      fetchGoalBank();
    } catch (error) {
      console.error('Error deleting goal:', error);
      toast.error('Failed to delete goal');
    }
  };

  const handleScaffoldingChange = async (newIndex) => {
    setScaffoldingIndex(newIndex);
    
    if (!result?.iep_goal?.annual_goal?.goal_text) return;
    
    const newLevel = scaffoldingLevels[newIndex].value;
    if (newLevel === 'none') {
      setAdjustedGoal(null);
      return;
    }

    setAdjustingScaffolding(true);
    try {
      const response = await axios.post(`${API}/adjust-goal-scaffolding`, {
        goal_text: result.iep_goal.annual_goal.goal_text,
        scaffolding_level: newLevel,
        goal_domain: result.goal_domain,
        grade_level: result.grade_level
      }, { withCredentials: true });
      
      setAdjustedGoal(response.data);
      toast.success(`Goal adjusted for ${scaffoldingLevels[newIndex].label} support`);
    } catch (error) {
      console.error('Error adjusting scaffolding:', error);
      toast.error('Failed to adjust goal');
    } finally {
      setAdjustingScaffolding(false);
    }
  };

  const handleStorySelect = (storyId) => {
    if (storyId === 'none') {
      setSelectedStory(null);
      return;
    }
    const story = stories.find(s => s.story_id === storyId);
    if (story) {
      setSelectedStory(story);
      setFormData({
        ...formData,
        scenario: story.scenario || '',
        situation_details: story.situation_details || '',
        grade_level: story.student_info?.grade || formData.grade_level,
        student_strengths: story.student_info?.strengths || '',
        student_interests: story.student_info?.interests || ''
      });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Reset scaffolding when generating new goal
    setScaffoldingIndex(0);
    setAdjustedGoal(null);
    
    if (goalSource === 'story') {
      if (!selectedStory) {
        toast.error('Please select a story to generate an IEP goal');
        return;
      }
      if (!formData.grade_level) {
        toast.error('Please select a grade level');
        return;
      }
    } else {
      // Standard-based goal
      if (!formData.goal_domain || !formData.grade_level) {
        toast.error('Please select a goal domain and grade level');
        return;
      }
    }

    setLoading(true);
    setResult(null);

    try {
      if (goalSource === 'story' && selectedStory) {
        const response = await axios.post(
          `${API}/stories/${selectedStory.story_id}/generate-iep-goal`,
          {
            include_objectives: formData.include_objectives,
            grade_level: formData.grade_level,
            custom_context: formData.custom_context
          },
          { withCredentials: true }
        );
        setResult(response.data);
      } else {
        // Standard-based goal generation
        const response = await axios.post(
          `${API}/generate-standalone-iep-goal`,
          {
            goal_domain: formData.goal_domain,
            grade_level: formData.grade_level,
            anchor_standard_type: formData.anchor_standard_type,
            anchor_standard: formData.anchor_standard,
            student_strengths: formData.student_strengths,
            include_objectives: formData.include_objectives,
            custom_context: formData.custom_context
          },
          { withCredentials: true }
        );
        setResult(response.data);
      }
      toast.success('IEP goal generated successfully!');
    } catch (error) {
      console.error('Error generating IEP goal:', error);
      toast.error('Failed to generate goal. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = () => {
    if (!result) return;
    
    const text = formatResultAsText(result);
    navigator.clipboard.writeText(text);
    setCopied(true);
    toast.success('Copied to clipboard!');
    setTimeout(() => setCopied(false), 2000);
  };

  const formatResultAsText = (data) => {
    const goal = data.iep_goal;
    let text = `IEP GOAL\n`;
    text += `Student: ${data.student_name}\n`;
    text += `Grade: ${data.grade_level}\n`;
    text += `SEL Standard: ${data.sel_standard}\n\n`;
    
    text += `ANNUAL GOAL\n`;
    text += `${goal.annual_goal?.goal_text}\n\n`;
    text += `Area of Need: ${goal.annual_goal?.area_of_need}\n`;
    text += `Baseline: ${goal.annual_goal?.baseline}\n`;
    text += `Target Criteria: ${goal.annual_goal?.target_criteria}\n`;
    text += `Measurement Method: ${goal.annual_goal?.measurement_method}\n`;
    text += `Timeframe: ${goal.annual_goal?.timeframe}\n\n`;
    
    if (goal.objective_1) {
      text += `OBJECTIVE 1\n${goal.objective_1.goal_text}\n\n`;
    }
    if (goal.objective_2) {
      text += `OBJECTIVE 2\n${goal.objective_2.goal_text}\n\n`;
    }
    
    text += `PROGRESS MONITORING\n${goal.progress_monitoring}\n\n`;
    
    if (goal.accommodations_suggestions?.length > 0) {
      text += `SUGGESTED ACCOMMODATIONS\n`;
      goal.accommodations_suggestions.forEach(a => {
        text += `• ${a}\n`;
      });
    }
    
    return text;
  };

  const downloadPDF = () => {
    if (!result) return;
    
    const doc = new jsPDF();
    const goal = result.iep_goal;
    const pageWidth = doc.internal.pageSize.getWidth();
    const margin = 20;
    const contentWidth = pageWidth - margin * 2;
    let yPos = 20;
    
    // Title
    doc.setFontSize(16);
    doc.setFont('helvetica', 'bold');
    doc.text('IEP GOAL', margin, yPos);
    yPos += 10;
    
    // Student Info
    doc.setFontSize(11);
    doc.setFont('helvetica', 'normal');
    doc.text(`Student: ${result.student_name}`, margin, yPos);
    yPos += 6;
    doc.text(`Grade: ${result.grade_level}`, margin, yPos);
    yPos += 6;
    doc.text(`SEL Standard: ${result.sel_standard}`, margin, yPos);
    yPos += 12;
    
    // Annual Goal
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(12);
    doc.text('ANNUAL GOAL', margin, yPos);
    yPos += 8;
    
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(10);
    const goalLines = doc.splitTextToSize(goal.annual_goal?.goal_text || '', contentWidth);
    goalLines.forEach(line => {
      if (yPos > 280) {
        doc.addPage();
        yPos = 20;
      }
      doc.text(line, margin, yPos);
      yPos += 5;
    });
    yPos += 5;
    
    // Goal Details
    const addDetail = (label, value) => {
      if (!value) return;
      if (yPos > 270) {
        doc.addPage();
        yPos = 20;
      }
      doc.setFont('helvetica', 'bold');
      doc.text(`${label}:`, margin, yPos);
      doc.setFont('helvetica', 'normal');
      const lines = doc.splitTextToSize(value, contentWidth - 40);
      doc.text(lines, margin + 40, yPos);
      yPos += Math.max(6, lines.length * 5);
    };
    
    addDetail('Area of Need', goal.annual_goal?.area_of_need);
    addDetail('Baseline', goal.annual_goal?.baseline);
    addDetail('Target', goal.annual_goal?.target_criteria);
    addDetail('Measurement', goal.annual_goal?.measurement_method);
    addDetail('Timeframe', goal.annual_goal?.timeframe);
    
    // Objectives
    if (goal.objective_1) {
      yPos += 5;
      doc.setFont('helvetica', 'bold');
      doc.text('OBJECTIVE 1', margin, yPos);
      yPos += 6;
      doc.setFont('helvetica', 'normal');
      const obj1Lines = doc.splitTextToSize(goal.objective_1.goal_text || '', contentWidth);
      obj1Lines.forEach(line => {
        if (yPos > 280) { doc.addPage(); yPos = 20; }
        doc.text(line, margin, yPos);
        yPos += 5;
      });
    }
    
    if (goal.objective_2) {
      yPos += 5;
      doc.setFont('helvetica', 'bold');
      doc.text('OBJECTIVE 2', margin, yPos);
      yPos += 6;
      doc.setFont('helvetica', 'normal');
      const obj2Lines = doc.splitTextToSize(goal.objective_2.goal_text || '', contentWidth);
      obj2Lines.forEach(line => {
        if (yPos > 280) { doc.addPage(); yPos = 20; }
        doc.text(line, margin, yPos);
        yPos += 5;
      });
    }
    
    // Progress Monitoring
    if (goal.progress_monitoring) {
      yPos += 5;
      doc.setFont('helvetica', 'bold');
      doc.text('PROGRESS MONITORING', margin, yPos);
      yPos += 6;
      doc.setFont('helvetica', 'normal');
      const pmLines = doc.splitTextToSize(goal.progress_monitoring, contentWidth);
      pmLines.forEach(line => {
        if (yPos > 280) { doc.addPage(); yPos = 20; }
        doc.text(line, margin, yPos);
        yPos += 5;
      });
    }
    
    // Accommodations
    if (goal.accommodations_suggestions?.length > 0) {
      yPos += 5;
      doc.setFont('helvetica', 'bold');
      doc.text('SUGGESTED ACCOMMODATIONS', margin, yPos);
      yPos += 6;
      doc.setFont('helvetica', 'normal');
      goal.accommodations_suggestions.forEach(a => {
        if (yPos > 280) { doc.addPage(); yPos = 20; }
        doc.text(`• ${a}`, margin, yPos);
        yPos += 5;
      });
    }
    
    // Footer
    doc.setFontSize(8);
    doc.setTextColor(128, 128, 128);
    doc.text(`Generated by SpEdKit - ${new Date().toLocaleDateString()}`, margin, 285);
    
    doc.save(`IEP_Goal_${result.student_name.replace(/\s+/g, '_')}.pdf`);
    toast.success('PDF downloaded!');
  };

  // ============== Additional Resources Functions ==============

  // Generate Progress Monitoring Worksheet
  const generateWorksheet = async () => {
    if (!result?.iep_goal?.annual_goal) return;
    
    setGeneratingWorksheet(true);
    try {
      const goal = result.iep_goal.annual_goal;
      const response = await axios.post(
        `${API}/generate-progress-monitoring-worksheet`,
        {
          goal_text: goal.goal_text,
          goal_domain: goal.area_of_need || formData.goal_domain,
          grade_level: result.grade_level,
          student_name: result.student_name,
          measurement_method: goal.measurement_method,
          target_criteria: goal.target_criteria
        },
        { withCredentials: true }
      );
      
      setWorksheetData(response.data);
      toast.success('Worksheet generated!');
    } catch (error) {
      console.error('Error generating worksheet:', error);
      toast.error('Failed to generate worksheet');
    } finally {
      setGeneratingWorksheet(false);
    }
  };

  // Download Progress Monitoring Worksheet as PDF
  const downloadWorksheetPdf = () => {
    if (!worksheetData?.worksheet) return;
    
    const ws = worksheetData.worksheet;
    const worksheetType = worksheetData.worksheet_type || 'general';
    const pdf = new jsPDF('portrait', 'mm', 'letter');
    const pageWidth = pdf.internal.pageSize.getWidth();
    const pageHeight = pdf.internal.pageSize.getHeight();
    const margin = 15;
    const contentWidth = pageWidth - 2 * margin;
    
    const darkSage = [58, 90, 64];
    
    pdf.setFillColor(255, 255, 255);
    pdf.rect(0, 0, pageWidth, pageHeight, 'F');
    
    let yPos = margin;
    
    // Title
    pdf.setFont('helvetica', 'bold');
    pdf.setFontSize(18);
    pdf.setTextColor(...darkSage);
    pdf.text(ws.worksheet_title || 'Practice Worksheet', pageWidth / 2, yPos + 8, { align: 'center' });
    yPos += 15;
    
    // Student info
    pdf.setFontSize(11);
    pdf.setTextColor(80, 80, 80);
    pdf.text(`Name: ${worksheetData.student_name || result?.student_name || '_________________'}`, margin, yPos);
    pdf.text(`Date: _________________`, pageWidth - margin - 50, yPos);
    yPos += 10;
    
    // Goal/Skill summary
    if (ws.goal_summary) {
      pdf.setFont('helvetica', 'bold');
      pdf.setFontSize(10);
      pdf.setTextColor(...darkSage);
      pdf.text('Skill:', margin, yPos);
      yPos += 5;
      pdf.setFont('helvetica', 'normal');
      pdf.setTextColor(60, 60, 60);
      const goalLines = pdf.splitTextToSize(ws.goal_summary, contentWidth);
      pdf.text(goalLines, margin, yPos);
      yPos += goalLines.length * 4 + 5;
    }
    
    // Instructions for student
    if (ws.instructions_for_student) {
      pdf.setFont('helvetica', 'bold');
      pdf.setFontSize(11);
      pdf.setTextColor(...darkSage);
      pdf.text('Instructions:', margin, yPos);
      yPos += 5;
      pdf.setFont('helvetica', 'normal');
      pdf.setFontSize(10);
      pdf.setTextColor(60, 60, 60);
      const instrLines = pdf.splitTextToSize(ws.instructions_for_student, contentWidth);
      pdf.text(instrLines, margin, yPos);
      yPos += instrLines.length * 4 + 8;
    }
    
    // Draw a separator line
    pdf.setDrawColor(200, 200, 200);
    pdf.setLineWidth(0.5);
    pdf.line(margin, yPos, pageWidth - margin, yPos);
    yPos += 8;
    
    // Handle different worksheet types
    if (worksheetType === 'reading' && ws.reading_passage) {
      // READING WORKSHEET
      // Passage title
      pdf.setFont('helvetica', 'bold');
      pdf.setFontSize(12);
      pdf.setTextColor(...darkSage);
      pdf.text(ws.reading_passage.title || 'Reading Passage', pageWidth / 2, yPos, { align: 'center' });
      yPos += 8;
      
      // Passage text
      pdf.setFont('helvetica', 'normal');
      pdf.setFontSize(10);
      pdf.setTextColor(40, 40, 40);
      const passageText = ws.reading_passage.text || '';
      const passageLines = pdf.splitTextToSize(passageText, contentWidth);
      
      passageLines.forEach((line) => {
        if (yPos > pageHeight - 30) {
          pdf.addPage();
          pdf.setFillColor(255, 255, 255);
          pdf.rect(0, 0, pageWidth, pageHeight, 'F');
          yPos = margin;
        }
        pdf.text(line, margin, yPos);
        yPos += 5;
      });
      
      yPos += 10;
      
      // Comprehension Questions
      if (ws.comprehension_questions?.length > 0) {
        if (yPos > pageHeight - 60) {
          pdf.addPage();
          pdf.setFillColor(255, 255, 255);
          pdf.rect(0, 0, pageWidth, pageHeight, 'F');
          yPos = margin;
        }
        
        pdf.setFont('helvetica', 'bold');
        pdf.setFontSize(12);
        pdf.setTextColor(...darkSage);
        pdf.text('Comprehension Questions', margin, yPos);
        yPos += 8;
        
        pdf.setFont('helvetica', 'normal');
        pdf.setFontSize(10);
        pdf.setTextColor(40, 40, 40);
        
        ws.comprehension_questions.forEach((q, i) => {
          if (yPos > pageHeight - 25) {
            pdf.addPage();
            pdf.setFillColor(255, 255, 255);
            pdf.rect(0, 0, pageWidth, pageHeight, 'F');
            yPos = margin;
          }
          
          const questionText = `${q.question_number || i + 1}. ${q.question_text}`;
          const qLines = pdf.splitTextToSize(questionText, contentWidth - 5);
          qLines.forEach((line) => {
            pdf.text(line, margin, yPos);
            yPos += 5;
          });
          
          // Answer line
          pdf.text(`Answer: ${q.answer_space || '________________________________________'}`, margin + 5, yPos);
          yPos += 12;
        });
      }
    } else if (worksheetType === 'math' && ws.problems?.length > 0) {
      // MATH WORKSHEET
      pdf.setFont('helvetica', 'bold');
      pdf.setFontSize(12);
      pdf.setTextColor(...darkSage);
      pdf.text('Problems', margin, yPos);
      yPos += 8;
      
      pdf.setFont('helvetica', 'normal');
      pdf.setFontSize(11);
      pdf.setTextColor(40, 40, 40);
      
      ws.problems.forEach((p, i) => {
        if (yPos > pageHeight - 25) {
          pdf.addPage();
          pdf.setFillColor(255, 255, 255);
          pdf.rect(0, 0, pageWidth, pageHeight, 'F');
          yPos = margin;
        }
        
        const problemText = `${p.problem_number || i + 1}. ${p.problem_text}`;
        const pLines = pdf.splitTextToSize(problemText, contentWidth - 10);
        pLines.forEach((line) => {
          pdf.text(line, margin, yPos);
          yPos += 6;
        });
        yPos += 8;
      });
    } else if (ws.practice_items?.length > 0) {
      // GENERAL PRACTICE WORKSHEET
      pdf.setFont('helvetica', 'bold');
      pdf.setFontSize(12);
      pdf.setTextColor(...darkSage);
      pdf.text('Practice Items', margin, yPos);
      yPos += 8;
      
      pdf.setFont('helvetica', 'normal');
      pdf.setFontSize(11);
      pdf.setTextColor(40, 40, 40);
      
      ws.practice_items.forEach((item, i) => {
        if (yPos > pageHeight - 25) {
          pdf.addPage();
          pdf.setFillColor(255, 255, 255);
          pdf.rect(0, 0, pageWidth, pageHeight, 'F');
          yPos = margin;
        }
        
        const itemText = `${item.item_number || i + 1}. ${item.item_text}`;
        const itemLines = pdf.splitTextToSize(itemText, contentWidth - 10);
        itemLines.forEach((line) => {
          pdf.text(line, margin, yPos);
          yPos += 6;
        });
        yPos += 8;
      });
    } else {
      // Fallback: Old format or unknown format - show scoring guide if available
      if (ws.scoring_guide?.scale) {
        pdf.setFont('helvetica', 'bold');
        pdf.setFontSize(10);
        pdf.setTextColor(...darkSage);
        pdf.text('Scoring Guide:', margin, yPos);
        yPos += 6;
        
        pdf.setFont('helvetica', 'normal');
        pdf.setFontSize(8);
        pdf.setTextColor(60, 60, 60);
        ws.scoring_guide.scale.forEach((item) => {
          pdf.text(`${item.score} = ${item.label}: ${item.description || ''}`, margin + 5, yPos);
          yPos += 4;
        });
      }
    }
    
    // Footer
    pdf.setFontSize(7);
    pdf.setTextColor(150, 150, 150);
    pdf.text('Created with SpEdKit', pageWidth / 2, pageHeight - 10, { align: 'center' });
    
    const fileName = worksheetType === 'reading' ? 'Reading_Worksheet' : 
                     worksheetType === 'math' ? 'Math_Worksheet' : 
                     'Practice_Worksheet';
    pdf.save(`${fileName}_${result?.student_name?.replace(/\s+/g, '_') || 'Student'}.pdf`);
    toast.success('Worksheet downloaded!');
  };

  // Generate Project-Based Learning Ideas
  const generatePBL = async () => {
    if (!result?.iep_goal?.annual_goal) return;
    
    setGeneratingPBL(true);
    try {
      const goal = result.iep_goal.annual_goal;
      const response = await axios.post(
        `${API}/generate-pbl-ideas`,
        {
          goal_text: goal.goal_text,
          goal_domain: goal.area_of_need || formData.goal_domain,
          grade_level: result.grade_level,
          student_interests: formData.student_strengths || null,
          subject_area: goal.area_of_need
        },
        { withCredentials: true }
      );
      
      setPblData(response.data);
      toast.success('Project ideas generated!');
    } catch (error) {
      console.error('Error generating PBL ideas:', error);
      toast.error('Failed to generate project ideas');
    } finally {
      setGeneratingPBL(false);
    }
  };

  // Download PBL Ideas as PDF
  const downloadPBLPdf = () => {
    if (!pblData?.pbl_ideas) return;
    
    const pbl = pblData.pbl_ideas;
    const pdf = new jsPDF('portrait', 'mm', 'letter');
    const pageWidth = pdf.internal.pageSize.getWidth();
    const pageHeight = pdf.internal.pageSize.getHeight();
    const margin = 15;
    const contentWidth = pageWidth - 2 * margin;
    
    const darkSage = [58, 90, 64];
    
    pdf.setFillColor(255, 255, 255);
    pdf.rect(0, 0, pageWidth, pageHeight, 'F');
    
    let yPos = margin;
    
    pdf.setFont('helvetica', 'bold');
    pdf.setFontSize(20);
    pdf.setTextColor(...darkSage);
    pdf.text('Project-Based Learning Ideas', pageWidth / 2, yPos + 8, { align: 'center' });
    yPos += 15;
    
    pdf.setFontSize(10);
    pdf.setTextColor(80, 80, 80);
    pdf.text(`Aligned to Goal: ${pblData.goal_domain}`, margin, yPos);
    pdf.text(`Grade: ${pblData.grade_level}`, pageWidth - margin - 40, yPos);
    yPos += 12;
    
    (pbl.projects || []).forEach((project, index) => {
      if (yPos > pageHeight - 60) {
        pdf.addPage();
        pdf.setFillColor(255, 255, 255);
        pdf.rect(0, 0, pageWidth, pageHeight, 'F');
        yPos = margin;
      }
      
      pdf.setFont('helvetica', 'bold');
      pdf.setFontSize(14);
      pdf.setTextColor(...darkSage);
      pdf.text(`${index + 1}. ${project.title}`, margin, yPos);
      yPos += 7;
      
      pdf.setFont('helvetica', 'normal');
      pdf.setFontSize(10);
      pdf.setTextColor(60, 60, 60);
      const descLines = pdf.splitTextToSize(project.description || '', contentWidth);
      pdf.text(descLines, margin, yPos);
      yPos += descLines.length * 4 + 5;
      
      if (project.duration) {
        pdf.setFont('helvetica', 'bold');
        pdf.setFontSize(9);
        pdf.text(`Duration: `, margin, yPos);
        pdf.setFont('helvetica', 'normal');
        pdf.text(project.duration, margin + 20, yPos);
        yPos += 5;
      }
      
      if (project.iep_goal_connection) {
        pdf.setFont('helvetica', 'bold');
        pdf.setFontSize(9);
        pdf.setTextColor(...darkSage);
        pdf.text('IEP Goal Connection:', margin, yPos);
        yPos += 4;
        pdf.setFont('helvetica', 'normal');
        pdf.setTextColor(60, 60, 60);
        const connLines = pdf.splitTextToSize(project.iep_goal_connection, contentWidth - 5);
        pdf.text(connLines, margin + 5, yPos);
        yPos += connLines.length * 4 + 8;
      }
      
      yPos += 5;
    });
    
    pdf.setFontSize(7);
    pdf.setTextColor(150, 150, 150);
    pdf.text('Created with SpEdKit', pageWidth / 2, pageHeight - 10, { align: 'center' });
    
    pdf.save(`PBL_Ideas_${pblData.goal_domain || 'Projects'}.pdf`);
    toast.success('Project ideas downloaded!');
  };

  // Generate Task Analysis Worksheet (for ADL goals)
  const generateTaskAnalysis = async () => {
    if (!result?.iep_goal?.annual_goal) return;
    
    setGeneratingTaskAnalysis(true);
    try {
      const goal = result.iep_goal.annual_goal;
      const response = await axios.post(
        `${API}/generate-task-analysis`,
        {
          goal_text: goal.goal_text,
          goal_domain: goal.area_of_need || formData.goal_domain,
          grade_level: result.grade_level,
          student_name: result.student_name
        },
        { withCredentials: true }
      );
      
      setTaskAnalysisData(response.data);
      toast.success('Task analysis generated!');
    } catch (error) {
      console.error('Error generating task analysis:', error);
      toast.error('Failed to generate task analysis');
    } finally {
      setGeneratingTaskAnalysis(false);
    }
  };

  // Download Task Analysis as PDF
  const downloadTaskAnalysisPdf = () => {
    if (!taskAnalysisData?.task_analysis) return;
    
    const ta = taskAnalysisData.task_analysis;
    const pdf = new jsPDF('portrait', 'mm', 'letter');
    const pageWidth = pdf.internal.pageSize.getWidth();
    const pageHeight = pdf.internal.pageSize.getHeight();
    const margin = 15;
    const contentWidth = pageWidth - 2 * margin;
    
    const darkSage = [58, 90, 64];
    
    pdf.setFillColor(255, 255, 255);
    pdf.rect(0, 0, pageWidth, pageHeight, 'F');
    
    let yPos = margin;
    
    // Title
    pdf.setFont('helvetica', 'bold');
    pdf.setFontSize(18);
    pdf.setTextColor(...darkSage);
    pdf.text('Task Analysis Worksheet', pageWidth / 2, yPos + 8, { align: 'center' });
    yPos += 12;
    
    // Task title
    pdf.setFontSize(14);
    pdf.text(ta.task_title || 'Daily Living Skill', pageWidth / 2, yPos + 5, { align: 'center' });
    yPos += 12;
    
    // Student info line
    pdf.setFontSize(10);
    pdf.setTextColor(80, 80, 80);
    pdf.text(`Student: ${taskAnalysisData.student_name || '_________________'}`, margin, yPos);
    pdf.text(`Date: _________________`, pageWidth - margin - 50, yPos);
    yPos += 8;
    
    // Category
    if (ta.skill_category) {
      pdf.setFont('helvetica', 'bold');
      pdf.setFontSize(9);
      pdf.setTextColor(...darkSage);
      pdf.text(`Category: ${ta.skill_category}`, margin, yPos);
      yPos += 8;
    }
    
    // Materials
    if (ta.materials_needed?.length > 0) {
      pdf.setFont('helvetica', 'bold');
      pdf.setFontSize(9);
      pdf.text('Materials Needed:', margin, yPos);
      pdf.setFont('helvetica', 'normal');
      pdf.setTextColor(60, 60, 60);
      pdf.text(ta.materials_needed.join(', '), margin + 35, yPos);
      yPos += 8;
    }
    
    // Steps table header
    pdf.setFillColor(245, 245, 245);
    pdf.rect(margin, yPos, contentWidth, 8, 'F');
    pdf.setFont('helvetica', 'bold');
    pdf.setFontSize(8);
    pdf.setTextColor(60, 60, 60);
    
    const stepCol = 10;
    const actionCol = 70;
    const promptCol = 50;
    const dataCol = contentWidth - stepCol - actionCol - promptCol;
    
    pdf.text('#', margin + 2, yPos + 5);
    pdf.text('Action Step', margin + stepCol + 2, yPos + 5);
    pdf.text('Verbal Prompt', margin + stepCol + actionCol + 2, yPos + 5);
    pdf.text('+/VP/GP/PP/FP', margin + stepCol + actionCol + promptCol + 2, yPos + 5);
    
    pdf.setDrawColor(200, 200, 200);
    pdf.setLineWidth(0.5);
    pdf.rect(margin, yPos, contentWidth, 8);
    yPos += 8;
    
    // Steps rows
    pdf.setFont('helvetica', 'normal');
    pdf.setFontSize(7);
    
    (ta.steps || []).forEach((step, i) => {
      if (yPos > pageHeight - 30) {
        pdf.addPage();
        pdf.setFillColor(255, 255, 255);
        pdf.rect(0, 0, pageWidth, pageHeight, 'F');
        yPos = margin;
      }
      
      const rowHeight = 10;
      pdf.rect(margin, yPos, contentWidth, rowHeight);
      pdf.line(margin + stepCol, yPos, margin + stepCol, yPos + rowHeight);
      pdf.line(margin + stepCol + actionCol, yPos, margin + stepCol + actionCol, yPos + rowHeight);
      pdf.line(margin + stepCol + actionCol + promptCol, yPos, margin + stepCol + actionCol + promptCol, yPos + rowHeight);
      
      pdf.setTextColor(60, 60, 60);
      pdf.text(String(step.step_number || i + 1), margin + 3, yPos + 6);
      
      const actionLines = pdf.splitTextToSize(step.action || '', actionCol - 4);
      pdf.text(actionLines[0] || '', margin + stepCol + 2, yPos + 6);
      
      const promptLines = pdf.splitTextToSize(step.verbal_prompt || '', promptCol - 4);
      pdf.text(promptLines[0] || '', margin + stepCol + actionCol + 2, yPos + 6);
      
      yPos += rowHeight;
    });
    
    yPos += 8;
    
    // Data collection info
    if (ta.data_collection) {
      if (yPos > pageHeight - 40) {
        pdf.addPage();
        pdf.setFillColor(255, 255, 255);
        pdf.rect(0, 0, pageWidth, pageHeight, 'F');
        yPos = margin;
      }
      
      pdf.setFont('helvetica', 'bold');
      pdf.setFontSize(9);
      pdf.setTextColor(...darkSage);
      pdf.text('Data Collection:', margin, yPos);
      yPos += 5;
      
      pdf.setFont('helvetica', 'normal');
      pdf.setFontSize(8);
      pdf.setTextColor(60, 60, 60);
      
      if (ta.data_collection.scoring) {
        pdf.text(`Scoring Key: ${ta.data_collection.scoring}`, margin + 5, yPos);
        yPos += 4;
      }
      if (ta.data_collection.mastery_criteria) {
        pdf.text(`Mastery: ${ta.data_collection.mastery_criteria}`, margin + 5, yPos);
        yPos += 4;
      }
    }
    
    // Footer
    pdf.setFontSize(7);
    pdf.setTextColor(150, 150, 150);
    pdf.text('Created with SpEdKit', pageWidth / 2, pageHeight - 10, { align: 'center' });
    
    pdf.save(`Task_Analysis_${ta.task_title || 'ADL_Skill'}.pdf`);
    toast.success('Task analysis downloaded!');
  };

  // Helper to check if goal is ADL/functional
  const isADLGoal = () => {
    const domain = result?.iep_goal?.annual_goal?.area_of_need?.toLowerCase() || formData.goal_domain?.toLowerCase() || '';
    const adlKeywords = ['daily living', 'adl', 'self-care', 'hygiene', 'functional', 'life skill', 'adaptive', 'self-help', 'toileting', 'dressing', 'feeding', 'grooming'];
    return adlKeywords.some(keyword => domain.includes(keyword));
  };

  // Helper to check if goal is academic
  const isAcademicGoal = () => {
    const domain = result?.iep_goal?.annual_goal?.area_of_need?.toLowerCase() || formData.goal_domain?.toLowerCase() || '';
    const academicKeywords = ['reading', 'math', 'writing', 'academic', 'literacy', 'numeracy', 'comprehension', 'fluency', 'calculation'];
    return academicKeywords.some(keyword => domain.includes(keyword));
  };

  return (
    <div className="min-h-screen bg-background" data-testid="iep-goals-page">
      {/* Header */}
      <header className="w-full px-6 py-4 bg-white shadow-soft">
        <div className="max-w-7xl mx-auto flex justify-between items-center">
          <div className="cursor-pointer" onClick={() => navigate('/dashboard')}>
            <SpEdKitLogo className="w-10 h-10" textClassName="text-2xl" />
          </div>
          <Button
            variant="ghost"
            onClick={() => navigate('/dashboard')}
            className="text-text-secondary hover:text-primary"
            data-testid="back-to-dashboard-btn"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Dashboard
          </Button>
        </div>
      </header>

      <main className="px-6 py-12 max-w-5xl mx-auto">
        <div className="mb-8 animate-fade-in-up">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-12 h-12 bg-indigo-100 rounded-xl flex items-center justify-center">
              <Target className="w-6 h-6 text-indigo-500" />
            </div>
            <div>
              <h1 className="font-heading font-bold text-3xl text-text-primary">
                IEP Goal Generator
              </h1>
              <p className="font-body text-text-secondary">
                Create SMART goals from stories or anchor standards
              </p>
            </div>
          </div>
        </div>

        <div className="grid lg:grid-cols-2 gap-8">
          {/* Input Form */}
          <div className="bg-white rounded-2xl p-8 shadow-card animate-fade-in-up">
            <h2 className="font-heading font-semibold text-xl text-text-primary mb-6">
              Goal Information
            </h2>
            
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Goal Source Tabs */}
              <Tabs value={goalSource} onValueChange={setGoalSource} className="w-full">
                <TabsList className="grid w-full grid-cols-2">
                  <TabsTrigger value="story" className="data-[state=active]:bg-indigo-100">
                    <BookOpen className="w-4 h-4 mr-2" />
                    From Story
                  </TabsTrigger>
                  <TabsTrigger value="standard" className="data-[state=active]:bg-indigo-100">
                    <Target className="w-4 h-4 mr-2" />
                    From Standard
                  </TabsTrigger>
                </TabsList>
                
                <TabsContent value="story" className="space-y-4 mt-4">
                  {/* Story Selection */}
                  <div>
                    <Label className="text-text-primary font-medium">
                      Select Story *
                    </Label>
                    <Select
                      value={selectedStory?.story_id || ''}
                      onValueChange={handleStorySelect}
                    >
                      <SelectTrigger className="mt-2" data-testid="story-select">
                        <SelectValue placeholder="Select a story to base the goal on" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">-- Select a story --</SelectItem>
                        {stories.map((story) => (
                          <SelectItem key={story.story_id} value={story.story_id}>
                            {story.title} - {story.student_info?.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    {stories.length === 0 && (
                      <p className="text-xs text-amber-600 mt-1">
                        No stories found. <button type="button" onClick={() => navigate('/create')} className="underline">Create a story</button> first, or use the "From Standard" tab.
                      </p>
                    )}
                  </div>

                  {selectedStory && (
                    <div className="bg-indigo-50 rounded-lg p-3">
                      <p className="text-sm text-indigo-700">
                        <strong>Scenario:</strong> {selectedStory.scenario}
                      </p>
                      <p className="text-sm text-indigo-600 mt-1">
                        <strong>Student:</strong> {selectedStory.student_info?.name} ({selectedStory.student_info?.grade})
                      </p>
                    </div>
                  )}
                </TabsContent>
                
                <TabsContent value="standard" className="space-y-4 mt-4">
                  {/* Goal Domain */}
                  <div>
                    <Label className="text-text-primary font-medium">
                      Goal Domain *
                    </Label>
                    <Select
                      value={formData.goal_domain}
                      onValueChange={(value) => setFormData({ ...formData, goal_domain: value })}
                    >
                      <SelectTrigger className="mt-2" data-testid="goal-domain-select">
                        <SelectValue placeholder="Select goal domain" />
                      </SelectTrigger>
                      <SelectContent>
                        {goalDomains.map((category) => (
                          <div key={category.category}>
                            <div className="px-2 py-1.5 text-xs font-semibold text-text-muted bg-secondary/50">
                              {category.category}
                            </div>
                            {category.items.map((item) => (
                              <SelectItem key={item} value={item}>{item}</SelectItem>
                            ))}
                          </div>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Anchor Standard Type */}
                  <div>
                    <Label className="text-text-primary font-medium">
                      Anchor Standard Framework (Optional)
                    </Label>
                    <Select
                      value={formData.anchor_standard_type}
                      onValueChange={(value) => setFormData({ ...formData, anchor_standard_type: value, anchor_standard: '' })}
                    >
                      <SelectTrigger className="mt-2" data-testid="standard-type-select">
                        <SelectValue placeholder="Select standard framework" />
                      </SelectTrigger>
                      <SelectContent>
                        {anchorStandardTypes.map((type) => (
                          <SelectItem key={type.value} value={type.value}>
                            <div>
                              <span className="font-medium">{type.label}</span>
                              <span className="text-xs text-text-muted ml-2">{type.description}</span>
                            </div>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Anchor Standard */}
                  {formData.anchor_standard_type && anchorStandards[formData.anchor_standard_type] && (
                    <div>
                      <Label className="text-text-primary font-medium">
                        Specific Standard
                      </Label>
                      <Select
                        value={formData.anchor_standard}
                        onValueChange={(value) => setFormData({ ...formData, anchor_standard: value })}
                      >
                        <SelectTrigger className="mt-2" data-testid="anchor-standard-select">
                          <SelectValue placeholder="Select specific standard" />
                        </SelectTrigger>
                        <SelectContent>
                          {anchorStandards[formData.anchor_standard_type].map((standard) => (
                            <SelectItem key={standard} value={standard}>{standard}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  )}

                  {/* Student Strengths for standalone */}
                  <div>
                    <Label htmlFor="student_strengths_standalone" className="text-text-primary font-medium">
                      Student Strengths (Optional)
                    </Label>
                    <Input
                      id="student_strengths_standalone"
                      placeholder="e.g., Visual learner, responds well to praise..."
                      value={formData.student_strengths}
                      onChange={(e) => setFormData({ ...formData, student_strengths: e.target.value })}
                      className="mt-2"
                    />
                  </div>
                </TabsContent>
              </Tabs>

              {/* Common Fields */}
              <div>
                <Label htmlFor="grade_level" className="text-text-primary font-medium">
                  Grade Level *
                </Label>
                <Select
                  value={formData.grade_level}
                  onValueChange={(value) => setFormData({ ...formData, grade_level: value })}
                >
                  <SelectTrigger className="mt-2" data-testid="grade-level-select">
                    <SelectValue placeholder="Select grade" />
                  </SelectTrigger>
                  <SelectContent>
                    {gradeOptions.map((grade) => (
                      <SelectItem key={grade} value={grade}>{grade}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="flex items-center space-x-2">
                <Checkbox
                  id="include_objectives"
                  checked={formData.include_objectives}
                  onCheckedChange={(checked) => setFormData({ ...formData, include_objectives: checked })}
                  data-testid="include-objectives-checkbox"
                />
                <Label htmlFor="include_objectives" className="text-text-secondary cursor-pointer">
                  Include two short-term objectives
                </Label>
              </div>

              <div>
                <Label htmlFor="custom_context" className="text-text-primary font-medium">
                  Additional Context (Optional)
                </Label>
                <Textarea
                  id="custom_context"
                  placeholder="Any other information that should be considered when generating the goal..."
                  value={formData.custom_context}
                  onChange={(e) => setFormData({ ...formData, custom_context: e.target.value })}
                  className="mt-2"
                  data-testid="custom-context-input"
                />
              </div>

              <Button
                type="submit"
                disabled={loading || (goalSource === 'story' && !selectedStory) || (goalSource === 'standard' && !formData.goal_domain)}
                className="w-full bg-indigo-500 text-white hover:bg-indigo-600 rounded-full py-3"
                data-testid="generate-iep-goal-btn"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                    Generating Goal...
                  </>
                ) : (
                  <>
                    <Target className="w-5 h-5 mr-2" />
                    Generate IEP Goal
                  </>
                )}
              </Button>

              {/* Goal Bank Button */}
              <Button
                type="button"
                variant="outline"
                onClick={() => setShowGoalBank(true)}
                className="w-full border-indigo-200 text-indigo-600 hover:bg-indigo-50"
                data-testid="open-goal-bank-btn"
              >
                <FolderOpen className="w-5 h-5 mr-2" />
                Open Goal Bank ({savedGoals.length})
              </Button>
            </form>
          </div>

          {/* Results */}
          <div className="animate-fade-in-up animation-delay-200">
            {result ? (
              <div className="bg-white rounded-2xl p-8 shadow-card">
                <div className="flex justify-between items-start mb-6">
                  <h2 className="font-heading font-semibold text-xl text-text-primary">
                    Generated IEP Goal
                  </h2>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setShowSaveDialog(true)}
                      className="text-indigo-600 border-indigo-200 hover:bg-indigo-50"
                      data-testid="save-to-goal-bank-btn"
                    >
                      <Save className="w-4 h-4 mr-2" />
                      Save
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={copyToClipboard}
                      className="text-text-secondary"
                      data-testid="copy-goal-btn"
                    >
                      {copied ? (
                        <CheckCircle2 className="w-4 h-4 mr-2 text-green-500" />
                      ) : (
                        <Copy className="w-4 h-4 mr-2" />
                      )}
                      {copied ? 'Copied!' : 'Copy'}
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={downloadPDF}
                      className="text-text-secondary"
                      data-testid="download-goal-btn"
                    >
                      <Download className="w-4 h-4 mr-2" />
                      PDF
                    </Button>
                  </div>
                </div>

                {/* Scaffolding Slider */}
                <div className="bg-gradient-to-r from-amber-50 to-orange-50 rounded-xl p-4 mb-6 border border-amber-200">
                  <div className="flex items-center gap-2 mb-3">
                    <Sliders className="w-5 h-5 text-amber-600" />
                    <h3 className="font-heading font-medium text-text-primary">Scaffolding / Adult Support Level</h3>
                  </div>
                  <div className="px-2">
                    <Slider
                      value={[scaffoldingIndex]}
                      onValueChange={(vals) => handleScaffoldingChange(vals[0])}
                      max={3}
                      step={1}
                      className="mb-3"
                      disabled={adjustingScaffolding}
                      data-testid="scaffolding-slider"
                    />
                    <div className="flex justify-between text-xs">
                      {scaffoldingLevels.map((level, i) => (
                        <div 
                          key={level.value}
                          className={`text-center w-1/4 ${i === scaffoldingIndex ? 'text-amber-700 font-medium' : 'text-text-muted'}`}
                        >
                          <div className="font-medium">{level.label}</div>
                          <div className="hidden sm:block text-[10px]">{level.description}</div>
                        </div>
                      ))}
                    </div>
                  </div>
                  {adjustingScaffolding && (
                    <div className="flex items-center gap-2 mt-3 text-amber-600 text-sm">
                      <Loader2 className="w-4 h-4 animate-spin" />
                      Adjusting goal for {scaffoldingLevels[scaffoldingIndex].label} support...
                    </div>
                  )}
                </div>

                <div className="space-y-6">
                  {/* SEL Standard Badge */}
                  <div className="inline-flex items-center gap-2 bg-indigo-100 text-indigo-700 px-3 py-1 rounded-full text-sm font-medium">
                    <Target className="w-4 h-4" />
                    {result.sel_standard}
                  </div>

                  {/* Annual Goal */}
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <h3 className="font-heading font-medium text-text-primary">Annual Goal</h3>
                      {adjustedGoal && scaffoldingIndex > 0 && (
                        <span className="text-xs bg-amber-100 text-amber-700 px-2 py-1 rounded-full">
                          {scaffoldingLevels[scaffoldingIndex].label} Support
                        </span>
                      )}
                    </div>
                    <div className={`rounded-xl p-4 border-l-4 ${adjustedGoal && scaffoldingIndex > 0 ? 'bg-amber-50 border-amber-500' : 'bg-indigo-50 border-indigo-500'}`}>
                      <p className="font-body text-text-primary">
                        {adjustedGoal && scaffoldingIndex > 0 
                          ? adjustedGoal.adjusted_goal?.adjusted_goal_text 
                          : result.iep_goal?.annual_goal?.goal_text}
                      </p>
                    </div>
                    {adjustedGoal && scaffoldingIndex > 0 && adjustedGoal.adjusted_goal?.support_conditions && (
                      <div className="mt-2 p-3 bg-amber-50/50 rounded-lg">
                        <p className="text-sm text-amber-700">
                          <strong>Support Conditions:</strong> {adjustedGoal.adjusted_goal.support_conditions}
                        </p>
                      </div>
                    )}
                  </div>

                  {/* Scaffolding Accommodations */}
                  {adjustedGoal && scaffoldingIndex > 0 && adjustedGoal.adjusted_goal?.scaffolding_accommodations?.length > 0 && (
                    <div className="bg-amber-50/50 rounded-xl p-4">
                      <h3 className="font-heading font-medium text-amber-700 mb-2">Scaffolding Accommodations</h3>
                      <ul className="space-y-1">
                        {adjustedGoal.adjusted_goal.scaffolding_accommodations.map((acc, i) => (
                          <li key={i} className="font-body text-amber-600 text-sm flex items-start gap-2">
                            <CheckCircle2 className="w-4 h-4 flex-shrink-0 mt-0.5" />
                            {acc}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {/* Goal Details Grid */}
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div className="bg-secondary/30 rounded-lg p-3">
                      <p className="font-body text-xs text-text-muted uppercase tracking-wide mb-1">Area of Need</p>
                      <p className="font-body text-text-primary text-sm">{result.iep_goal?.annual_goal?.area_of_need}</p>
                    </div>
                    <div className="bg-secondary/30 rounded-lg p-3">
                      <p className="font-body text-xs text-text-muted uppercase tracking-wide mb-1">Timeframe</p>
                      <p className="font-body text-text-primary text-sm">{result.iep_goal?.annual_goal?.timeframe}</p>
                    </div>
                    <div className="bg-secondary/30 rounded-lg p-3">
                      <p className="font-body text-xs text-text-muted uppercase tracking-wide mb-1">Baseline</p>
                      <p className="font-body text-text-primary text-sm">{result.iep_goal?.annual_goal?.baseline}</p>
                    </div>
                    <div className="bg-secondary/30 rounded-lg p-3">
                      <p className="font-body text-xs text-text-muted uppercase tracking-wide mb-1">Target Criteria</p>
                      <p className="font-body text-text-primary text-sm">{result.iep_goal?.annual_goal?.target_criteria}</p>
                    </div>
                  </div>

                  <div className="bg-secondary/30 rounded-lg p-3">
                    <p className="font-body text-xs text-text-muted uppercase tracking-wide mb-1">Measurement Method</p>
                    <p className="font-body text-text-primary text-sm">{result.iep_goal?.annual_goal?.measurement_method}</p>
                  </div>

                  {/* Objectives */}
                  {(result.iep_goal?.objective_1 || result.iep_goal?.objective_2) && (
                    <div>
                      <button
                        type="button"
                        onClick={() => setShowObjectives(!showObjectives)}
                        className="flex items-center gap-2 font-heading font-medium text-text-primary mb-3"
                      >
                        Short-Term Objectives
                        {showObjectives ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                      </button>
                      
                      {showObjectives && (
                        <div className="space-y-3">
                          {result.iep_goal?.objective_1 && (
                            <div className="bg-green-50 rounded-lg p-4 border-l-4 border-green-400">
                              <p className="font-body text-xs text-green-600 uppercase tracking-wide mb-1">Objective 1</p>
                              <p className="font-body text-green-700 text-sm">{result.iep_goal.objective_1.goal_text}</p>
                            </div>
                          )}
                          {result.iep_goal?.objective_2 && (
                            <div className="bg-blue-50 rounded-lg p-4 border-l-4 border-blue-400">
                              <p className="font-body text-xs text-blue-600 uppercase tracking-wide mb-1">Objective 2</p>
                              <p className="font-body text-blue-700 text-sm">{result.iep_goal.objective_2.goal_text}</p>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  )}

                  {/* Progress Monitoring */}
                  {result.iep_goal?.progress_monitoring && (
                    <div>
                      <h3 className="font-heading font-medium text-text-primary mb-2">Progress Monitoring</h3>
                      <p className="font-body text-text-secondary text-sm bg-amber-50 rounded-lg p-4">
                        {result.iep_goal.progress_monitoring}
                      </p>
                    </div>
                  )}

                  {/* Accommodations */}
                  {result.iep_goal?.accommodations_suggestions?.length > 0 && scaffoldingIndex === 0 && (
                    <div>
                      <h3 className="font-heading font-medium text-text-primary mb-2">Suggested Accommodations</h3>
                      <ul className="space-y-2">
                        {result.iep_goal.accommodations_suggestions.map((acc, i) => (
                          <li key={i} className="font-body text-text-secondary text-sm flex items-start gap-2">
                            <CheckCircle2 className="w-4 h-4 text-green-500 flex-shrink-0 mt-0.5" />
                            {acc}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {/* Implementation Notes from Scaffolding */}
                  {adjustedGoal && scaffoldingIndex > 0 && adjustedGoal.adjusted_goal?.implementation_notes && (
                    <div className="bg-blue-50 rounded-xl p-4">
                      <h3 className="font-heading font-medium text-blue-700 mb-2">Implementation Notes</h3>
                      <p className="font-body text-blue-600 text-sm">
                        {adjustedGoal.adjusted_goal.implementation_notes}
                      </p>
                    </div>
                  )}

                  {/* Additional Resources Section */}
                  <div className="mt-8 pt-6 border-t-2 border-secondary/50">
                    <h3 className="font-heading font-semibold text-text-primary mb-4 flex items-center gap-2">
                      <BookOpen className="w-5 h-5 text-primary" />
                      Additional Resources
                    </h3>
                    <p className="text-sm text-text-muted mb-4">
                      Generate supplementary materials aligned to this IEP goal
                    </p>
                    
                    <div className="grid gap-3">
                      {/* Progress Monitoring Worksheet - Always show */}
                      <div className="bg-blue-50 rounded-xl p-4 border border-blue-200">
                        <div className="flex items-start justify-between">
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                              <ClipboardList className="w-5 h-5 text-blue-600" />
                            </div>
                            <div>
                              <p className="font-medium text-blue-800">Progress Monitoring Worksheet</p>
                              <p className="text-xs text-blue-600">Data collection sheet for tracking student progress</p>
                            </div>
                          </div>
                          {!worksheetData ? (
                            <Button
                              onClick={generateWorksheet}
                              disabled={generatingWorksheet}
                              size="sm"
                              className="bg-blue-600 text-white hover:bg-blue-700"
                              data-testid="generate-worksheet-btn"
                            >
                              {generatingWorksheet ? (
                                <Loader2 className="w-4 h-4 animate-spin" />
                              ) : (
                                'Generate'
                              )}
                            </Button>
                          ) : (
                            <Button
                              onClick={downloadWorksheetPdf}
                              size="sm"
                              variant="outline"
                              className="border-blue-600 text-blue-600 hover:bg-blue-50"
                              data-testid="download-worksheet-btn"
                            >
                              <Download className="w-4 h-4 mr-1" />
                              Download
                            </Button>
                          )}
                        </div>
                        {worksheetData && (
                          <p className="text-xs text-blue-600 mt-2 ml-13 italic">
                            ✓ Ready: {worksheetData.worksheet?.worksheet_title || 'Progress Monitoring'}
                          </p>
                        )}
                      </div>

                      {/* Project-Based Learning - Show for academic goals or always */}
                      <div className="bg-amber-50 rounded-xl p-4 border border-amber-200">
                        <div className="flex items-start justify-between">
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center">
                              <Lightbulb className="w-5 h-5 text-amber-600" />
                            </div>
                            <div>
                              <p className="font-medium text-amber-800">Project-Based Learning Ideas</p>
                              <p className="text-xs text-amber-600">Hands-on projects aligned to this goal {isAcademicGoal() ? '(Recommended for academic goals)' : ''}</p>
                            </div>
                          </div>
                          {!pblData ? (
                            <Button
                              onClick={generatePBL}
                              disabled={generatingPBL}
                              size="sm"
                              className="bg-amber-600 text-white hover:bg-amber-700"
                              data-testid="generate-pbl-btn"
                            >
                              {generatingPBL ? (
                                <Loader2 className="w-4 h-4 animate-spin" />
                              ) : (
                                'Generate'
                              )}
                            </Button>
                          ) : (
                            <Button
                              onClick={downloadPBLPdf}
                              size="sm"
                              variant="outline"
                              className="border-amber-600 text-amber-600 hover:bg-amber-50"
                              data-testid="download-pbl-btn"
                            >
                              <Download className="w-4 h-4 mr-1" />
                              Download
                            </Button>
                          )}
                        </div>
                        {pblData?.pbl_ideas?.projects && (
                          <p className="text-xs text-amber-600 mt-2 ml-13 italic">
                            ✓ {pblData.pbl_ideas.projects.length} project ideas ready
                          </p>
                        )}
                      </div>

                      {/* Task Analysis Worksheet - Show for ADL/functional goals or always */}
                      <div className="bg-emerald-50 rounded-xl p-4 border border-emerald-200">
                        <div className="flex items-start justify-between">
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 bg-emerald-100 rounded-lg flex items-center justify-center">
                              <ListChecks className="w-5 h-5 text-emerald-600" />
                            </div>
                            <div>
                              <p className="font-medium text-emerald-800">Task Analysis Worksheet</p>
                              <p className="text-xs text-emerald-600">Step-by-step breakdown for ADL/functional skills {isADLGoal() ? '(Recommended for this goal)' : ''}</p>
                            </div>
                          </div>
                          {!taskAnalysisData ? (
                            <Button
                              onClick={generateTaskAnalysis}
                              disabled={generatingTaskAnalysis}
                              size="sm"
                              className="bg-emerald-600 text-white hover:bg-emerald-700"
                              data-testid="generate-task-analysis-btn"
                            >
                              {generatingTaskAnalysis ? (
                                <Loader2 className="w-4 h-4 animate-spin" />
                              ) : (
                                'Generate'
                              )}
                            </Button>
                          ) : (
                            <Button
                              onClick={downloadTaskAnalysisPdf}
                              size="sm"
                              variant="outline"
                              className="border-emerald-600 text-emerald-600 hover:bg-emerald-50"
                              data-testid="download-task-analysis-btn"
                            >
                              <Download className="w-4 h-4 mr-1" />
                              Download
                            </Button>
                          )}
                        </div>
                        {taskAnalysisData && (
                          <p className="text-xs text-emerald-600 mt-2 ml-13 italic">
                            ✓ Ready: {taskAnalysisData.task_analysis?.task_title || 'Task Analysis'}
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              <div className="bg-secondary/30 rounded-2xl p-12 text-center">
                <div className="w-16 h-16 bg-indigo-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Target className="w-8 h-8 text-indigo-500" />
                </div>
                <h3 className="font-heading font-semibold text-text-primary mb-2">
                  Ready to Generate Goals
                </h3>
                <p className="font-body text-text-secondary text-sm max-w-sm mx-auto mb-4">
                  Select a story or standard to generate a SMART IEP goal, or load one from your Goal Bank.
                </p>
                {savedGoals.length > 0 && (
                  <Button
                    variant="outline"
                    onClick={() => setShowGoalBank(true)}
                    className="text-indigo-600 border-indigo-200"
                  >
                    <FolderOpen className="w-4 h-4 mr-2" />
                    Open Goal Bank ({savedGoals.length} saved)
                  </Button>
                )}
              </div>
            )}
          </div>
        </div>
      </main>

      {/* Save to Goal Bank Dialog */}
      <Dialog open={showSaveDialog} onOpenChange={setShowSaveDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Save className="w-5 h-5 text-indigo-500" />
              Save to Goal Bank
            </DialogTitle>
            <DialogDescription>
              Save this goal to your Goal Bank for easy reuse with other students.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label htmlFor="goal-title" className="text-text-primary font-medium">
                Goal Title *
              </Label>
              <Input
                id="goal-title"
                placeholder="e.g., Emotional Regulation - Frustration Management"
                value={goalTitle}
                onChange={(e) => setGoalTitle(e.target.value)}
                className="mt-2"
                data-testid="goal-title-input"
              />
            </div>
            <div>
              <Label className="text-text-primary font-medium">
                Add to Student Folder (Optional)
              </Label>
              <Select value={selectedFolderId} onValueChange={setSelectedFolderId}>
                <SelectTrigger className="mt-2" data-testid="folder-select">
                  <SelectValue placeholder="Select a student folder..." />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="">No folder (Goal Bank only)</SelectItem>
                  {folders.map((folder) => (
                    <SelectItem key={folder.folder_id} value={folder.folder_id}>
                      {folder.student_name} - {folder.grade || 'Grade not set'}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-text-muted mt-1">
                Link this goal to a student's folder for organization
              </p>
            </div>
            {scaffoldingIndex > 0 && (
              <div className="bg-amber-50 rounded-lg p-3">
                <p className="text-sm text-amber-700">
                  <strong>Note:</strong> This goal will be saved with <strong>{scaffoldingLevels[scaffoldingIndex].label}</strong> scaffolding level.
                </p>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowSaveDialog(false); setSelectedFolderId(''); }}>
              Cancel
            </Button>
            <Button 
              onClick={handleSaveToGoalBank}
              disabled={savingGoal || !goalTitle.trim()}
              className="bg-indigo-500 text-white hover:bg-indigo-600"
              data-testid="confirm-save-goal-btn"
            >
              {savingGoal ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <Save className="w-4 h-4 mr-2" />
                  Save Goal
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Goal Bank Dialog */}
      <Dialog open={showGoalBank} onOpenChange={setShowGoalBank}>
        <DialogContent className="sm:max-w-2xl max-h-[80vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FolderOpen className="w-5 h-5 text-indigo-500" />
              Goal Bank
            </DialogTitle>
            <DialogDescription>
              Your saved goals for quick reuse across students.
            </DialogDescription>
          </DialogHeader>
          <div className="flex-1 overflow-y-auto py-4">
            {loadingGoalBank ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-6 h-6 animate-spin text-indigo-500" />
              </div>
            ) : savedGoals.length === 0 ? (
              <div className="text-center py-12">
                <div className="w-16 h-16 bg-secondary/50 rounded-full flex items-center justify-center mx-auto mb-4">
                  <FolderOpen className="w-8 h-8 text-text-muted" />
                </div>
                <h3 className="font-heading font-semibold text-text-primary mb-2">
                  No Saved Goals
                </h3>
                <p className="font-body text-text-secondary text-sm">
                  Generate a goal and click "Save" to add it to your Goal Bank.
                </p>
              </div>
            ) : (
              <div className="space-y-3">
                {savedGoals.map((goal) => (
                  <div 
                    key={goal.goal_id}
                    className="bg-white border border-secondary rounded-xl p-4 hover:shadow-card transition-all"
                    data-testid={`saved-goal-${goal.goal_id}`}
                  >
                    <div className="flex justify-between items-start mb-2">
                      <div>
                        <h4 className="font-heading font-semibold text-text-primary">
                          {goal.title}
                        </h4>
                        <div className="flex flex-wrap gap-2 mt-1">
                          <span className="text-xs bg-indigo-100 text-indigo-600 px-2 py-0.5 rounded-full">
                            {goal.goal_domain}
                          </span>
                          <span className="text-xs bg-secondary text-text-secondary px-2 py-0.5 rounded-full">
                            {goal.grade_level}
                          </span>
                          {goal.scaffolding_level !== 'none' && (
                            <span className="text-xs bg-amber-100 text-amber-600 px-2 py-0.5 rounded-full">
                              {scaffoldingLevels.find(s => s.value === goal.scaffolding_level)?.label} Support
                            </span>
                          )}
                        </div>
                      </div>
                      <div className="flex gap-1">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleLoadFromGoalBank(goal)}
                          className="text-indigo-600 hover:bg-indigo-50"
                          data-testid={`load-goal-${goal.goal_id}`}
                        >
                          Use
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDeleteFromGoalBank(goal.goal_id)}
                          className="text-red-500 hover:bg-red-50"
                          data-testid={`delete-goal-${goal.goal_id}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                    <p className="font-body text-text-secondary text-sm line-clamp-2">
                      {goal.annual_goal?.goal_text || goal.annual_goal?.adjusted_goal_text}
                    </p>
                    <p className="text-xs text-text-muted mt-2">
                      Used {goal.use_count || 0} times • Saved {new Date(goal.created_at).toLocaleDateString()}
                    </p>
                  </div>
                ))}
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowGoalBank(false)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default IEPGoals;
